// services/frontend/src/utils/carrierResolver.js
import { toGcsPublicUrl, GCS_PUBLIC_BUCKET } from './mediaPaths';

/**
 * Normalizes a language code into either 'en' or 'fr-CA'.
 * @param {string} langCode - Raw language code
 * @returns {string} Normalized language code
 */
export const normalizeLanguage = (langCode) => {
  if (!langCode) return 'en';
  const lower = String(langCode).trim().toLowerCase();
  if (lower === 'qc' || lower === 'fr-qc' || lower === 'fr-ca' || lower === 'fr-fr' || lower === 'fr') {
    return 'fr-CA';
  }
  if (lower.startsWith('fr')) {
    return 'fr-CA';
  }
  return 'en';
};

/**
 * Checks if the language is a French variant.
 * @param {string} langCode - Raw language code
 * @returns {boolean} True if French/Québécois
 */
export const isFrenchLanguage = (langCode) => {
  return normalizeLanguage(langCode) === 'fr-CA';
};

const FRENCH_CARRIER_PATTERN = /(?:_|-)fr-ca(?:\.(?:mp3|json)|$|[?#])/i;

/**
 * Returns the language explicitly assigned to a Chronicle record. Filename
 * suffixes are authoritative for legacy records that predate language fields.
 */
export const getChronicleLanguage = (chronicle) => {
  if (!chronicle) return 'en';
  const explicit = chronicle.language || chronicle.locale
    || chronicle.metadata?.language || chronicle.metadata?.locale;
  if (explicit) return normalizeLanguage(explicit);

  const identityFields = [
    chronicle.key,
    chronicle.id,
    chronicle.filename,
    chronicle.basename,
    chronicle.url,
    chronicle.audioUrl,
  ];
  return identityFields.some(value => FRENCH_CARRIER_PATTERN.test(String(value || '')))
    ? 'fr-CA'
    : 'en';
};

/**
 * Retrieves the carrier object for the given language from the chronicle.
 * @param {object} chronicle - The chronicle object
 * @param {string} langCode - The target language
 * @returns {object|null} Carrier object or null
 */
export const getCarrier = (chronicle, langCode) => {
  if (!chronicle) return null;
  const normalized = normalizeLanguage(langCode);
  const carriers = chronicle.carriers || chronicle.metadata?.carriers || {};
  return carriers[normalized] || null;
};

/**
 * Checks if the Québécois audio carrier has a real asset.
 * @param {object} chronicle - The chronicle object
 * @returns {boolean} True if QC audio asset is available
 */
export const isQuebecoisAudioAvailable = (chronicle) => {
  if (!chronicle) return false;
  
  const frCarrier = getCarrier(chronicle, 'fr-CA');
  const enCarrier = getCarrier(chronicle, 'en');
  const enAudioUrl = enCarrier?.audioUrl || chronicle.url || chronicle.audioUrl || '';

  if (frCarrier) {
    const frAudio = frCarrier.audioUrl || frCarrier.audioPath || '';
    const isExplicitlyEnglish = frAudio && enAudioUrl && frAudio === enAudioUrl && !FRENCH_CARRIER_PATTERN.test(frAudio);

    if (frAudio && !isExplicitlyEnglish) {
      return true;
    }
    if (frCarrier.audioReady === true && !isExplicitlyEnglish) {
      return true;
    }
    if (frCarrier.status === 'rendered' && (frCarrier.type === 'audio' || frCarrier.audioReady === true) && !isExplicitlyEnglish) {
      return true;
    }
  }

  // Check candidates list for explicit QC naming patterns
  const candidates = Array.isArray(chronicle.candidates) ? chronicle.candidates : [];
  if (candidates.some(c => typeof c === 'string' && FRENCH_CARRIER_PATTERN.test(c))) {
    return true;
  }

  // Check main URL
  const primaryUrl = chronicle.url || chronicle.audioUrl || '';
  if (typeof primaryUrl === 'string' && FRENCH_CARRIER_PATTERN.test(primaryUrl)) {
    return true;
  }

  return false;
};

/**
 * Checks if the Québécois video carrier has a real asset.
 * @param {object} chronicle - The chronicle object
 * @returns {boolean} True if QC video asset is available
 */
export const isQuebecoisVideoAvailable = (chronicle) => {
  if (!chronicle) return false;

  const frCarrier = getCarrier(chronicle, 'fr-CA');
  if (frCarrier) {
    if (frCarrier.videoReady === true) {
      return true;
    }
    if (frCarrier.youtubeUrl || frCarrier.youtubeVideoId || frCarrier.youtubeVideo) {
      return true;
    }
    if (frCarrier.status === 'rendered' || frCarrier.status === 'uploaded') {
      // For video, a status of rendered/uploaded on the fr-CA carrier proves availability if it is the video carrier
      if (frCarrier.type === 'video' || frCarrier.youtubeUrl || frCarrier.videoPath) {
        return true;
      }
      // If type is omitted, assume status applies to video since video is the default carrier target
      if (frCarrier.type !== 'audio') {
        return true;
      }
    }
  }

  return false;
};

/**
 * Convenience union check for any Québécois carrier availability.
 * @param {object} chronicle - The chronicle object
 * @returns {boolean} True if either audio or video QC carrier is available
 */
export const isQuebecoisAvailable = (chronicle) => {
  return isQuebecoisAudioAvailable(chronicle) || isQuebecoisVideoAvailable(chronicle);
};

export const catchArtistVenueAudioMismatch = (profileOrChronicle, audioUrl) => {
  if (!profileOrChronicle || !audioUrl) return { isMismatch: false, venueId: null, notice: null };

  const isArtist = profileOrChronicle?.sourceType === 'artist' || profileOrChronicle?.role?.includes('Artist') || String(profileOrChronicle?.id || '').startsWith('artist-');
  const isVenueAudio = /\/transmission_venue-|\/venue-/.test(audioUrl);

  if (isArtist && isVenueAudio) {
    const match = audioUrl.match(/transmission_venue-([^._]+)/) || audioUrl.match(/venue-([^._]+)/);
    const venueSlug = match ? match[1] : 'associated-venue';
    const venueName = venueSlug.replace(/-/g, ' ').toUpperCase();
    return {
      isMismatch: true,
      venueId: `venue-${venueSlug}`,
      notice: `Receiver tuned to associated venue signal (${venueName}) while artist field capture is off-air.`,
    };
  }

  return { isMismatch: false, venueId: null, notice: null };
};

/**
 * Resolves the audio candidate playlist array for the selected language.
 * @param {object} chronicle - The chronicle object
 * @param {string} langCode - Stored language preference
 * @returns {string[]} Ordered list of audio urls
 */
export const resolveAudioCandidates = (chronicle, langCode) => {
  if (!chronicle) return [];
  
  const isFr = isFrenchLanguage(langCode);
  const rawCandidates = Array.isArray(chronicle.candidates)
    ? [...chronicle.candidates]
    : (chronicle.url ? [chronicle.url] : (chronicle.audioUrl ? [chronicle.audioUrl] : []));

  if (chronicle.fallbackAudioUrl && !rawCandidates.includes(chronicle.fallbackAudioUrl)) {
    rawCandidates.push(chronicle.fallbackAudioUrl);
  }

  // If this is an artist chronicle, prioritize artist audio over venue fallback audio
  let baseCandidates = rawCandidates;
  if (chronicle.sourceType === 'artist') {
    const artistCandidates = rawCandidates.filter(c => typeof c === 'string' && !/\/transmission_venue-|\/venue-/.test(c));
    const venueCandidates = rawCandidates.filter(c => typeof c === 'string' && /\/transmission_venue-|\/venue-/.test(c));
    baseCandidates = [...artistCandidates, ...venueCandidates];
  }

  if (isFr) {
    if (isQuebecoisAudioAvailable(chronicle)) {
      const frCarrier = getCarrier(chronicle, 'fr-CA');
      const enCarrier = getCarrier(chronicle, 'en');
      const enAudioUrl = enCarrier?.audioUrl || chronicle.url || chronicle.audioUrl || '';
      const qcList = [];

      if (frCarrier) {
        if (frCarrier.audioUrl) {
          const isEnDuplicate = frCarrier.audioUrl === enAudioUrl && !FRENCH_CARRIER_PATTERN.test(frCarrier.audioUrl);
          if (!isEnDuplicate) {
            qcList.push(frCarrier.audioUrl);
          }
        }
        if (frCarrier.audioPath) {
          const path = frCarrier.audioPath;
          const converted = (path.startsWith('http://') || path.startsWith('https://'))
            ? path
            : toGcsPublicUrl(GCS_PUBLIC_BUCKET, path);
          const isEnDuplicate = converted === enAudioUrl && !FRENCH_CARRIER_PATTERN.test(converted);
          if (!isEnDuplicate) {
            qcList.push(converted);
          }
        }
        if (Array.isArray(frCarrier.candidates)) {
          const valid = frCarrier.candidates.filter(c => typeof c === 'string' && (FRENCH_CARRIER_PATTERN.test(c) || c !== enAudioUrl));
          qcList.push(...valid);
        }
      }

      // Include standard candidates containing _fr-ca.mp3
      const explicitQc = baseCandidates.filter(c => typeof c === 'string' && FRENCH_CARRIER_PATTERN.test(c));
      qcList.push(...explicitQc);

      // Check primary URL if it is French
      const primaryUrl = chronicle.url || chronicle.audioUrl || '';
      if (typeof primaryUrl === 'string' && FRENCH_CARRIER_PATTERN.test(primaryUrl)) {
        qcList.push(primaryUrl);
      }

      if (qcList.length > 0) {
        return Array.from(new Set(qcList));
      }
    }

    return [];
  } else {
    // English language is selected: check en carrier first
    const enCarrier = getCarrier(chronicle, 'en');
    const enList = [];

    if (enCarrier) {
      if (enCarrier.audioUrl) {
        enList.push(enCarrier.audioUrl);
      }
      if (enCarrier.audioPath) {
        const path = enCarrier.audioPath;
        const converted = (path.startsWith('http://') || path.startsWith('https://'))
          ? path
          : toGcsPublicUrl(GCS_PUBLIC_BUCKET, path);
        enList.push(converted);
      }
      if (Array.isArray(enCarrier.candidates)) {
        enList.push(...enCarrier.candidates);
      }
    }

    // Clean base candidates containing _fr-ca.mp3 so they don't play when English is active
    const explicitEn = baseCandidates.filter(c => typeof c === 'string' && !FRENCH_CARRIER_PATTERN.test(c));
    enList.push(...explicitEn);

    if (enList.length > 0) {
      return Array.from(new Set(enList));
    }
  }

  // English fallback: clean out any _fr-ca.mp3 candidates so they don't play when English is active
  const filtered = baseCandidates.filter(c => typeof c === 'string' && !FRENCH_CARRIER_PATTERN.test(c));
  return Array.from(new Set(filtered));
};

/**
 * A Chronicle is visible only when it has a playable carrier for the selected
 * interface language. This keeps separately indexed `_fr-ca` renders out of
 * the English menu while retaining bilingual records with an explicit EN
 * carrier.
 */
export const isChronicleAvailableForLanguage = (chronicle, langCode) => {
  if (!chronicle) return false;
  if (isFrenchLanguage(langCode)) {
    if (isQuebecoisAudioAvailable(chronicle)) {
      return true;
    }
    // If explicitly marked as English-only (e.g. separate English-only render or test record), omit in French mode
    const key = String(chronicle.key || chronicle.id || chronicle.filename || '');
    if (/-english\b/i.test(key)) {
      return false;
    }
    // Cardinal chronicles and general broadcasts remain available with audio fallback
    if (chronicle.isCardinal) {
      return true;
    }
    return resolveAudioCandidates(chronicle, 'fr-CA').length > 0;
  }

  // English language mode
  const key = String(chronicle.key || chronicle.id || chronicle.filename || '');
  if (/-french\b/i.test(key)) {
    return false;
  }
  return resolveAudioCandidates(chronicle, 'en').length > 0;
};

export const filterChroniclesByLanguage = (chronicles, langCode) => {
  if (!Array.isArray(chronicles)) return [];
  const isFr = isFrenchLanguage(langCode);
  const filtered = chronicles.filter(chronicle => isChronicleAvailableForLanguage(chronicle, langCode));
  const list = filtered.length > 0
    ? filtered
    : chronicles;

  return list.map(chronicle => {
    const candidates = resolveAudioCandidates(chronicle, langCode);
    const resolvedUrl = candidates[0] || (isFr && !chronicle.isCardinal ? '' : (chronicle.url || chronicle.audioUrl || ''));
    return {
      ...chronicle,
      url: resolvedUrl,
      audioUrl: resolvedUrl,
      candidates,
    };
  });
};

/**
 * Resolves the primary audio URL for the chronicle and active language.
 * @param {object} chronicle - The chronicle object
 * @param {string} langCode - Language code
 * @returns {string} Resolved audio URL or empty string
 */
export const resolveAudioUrl = (chronicle, langCode) => {
  const candidates = resolveAudioCandidates(chronicle, langCode);
  return candidates[0] || '';
};

/**
 * Resolves the video metadata, returning a stable normalized object including fallback status.
 * @param {object} chronicle - The chronicle object
 * @param {string} langCode - Active language code
 * @returns {object} Normalized video details
 */
export const resolveVideoMetadata = (chronicle, langCode) => {
  const defaultMeta = {
    youtubeUrl: '',
    youtubeVideoId: '',
    videoPath: '',
    status: 'idle',
    fallback: true,
  };

  if (!chronicle) return defaultMeta;

  const isFr = isFrenchLanguage(langCode);
  if (isFr && isQuebecoisVideoAvailable(chronicle)) {
    const frCarrier = getCarrier(chronicle, 'fr-CA');
    if (frCarrier) {
      return {
        youtubeUrl: frCarrier.youtubeUrl || '',
        youtubeVideoId: frCarrier.youtubeVideoId || frCarrier.youtubeVideo || '',
        videoPath: frCarrier.videoPath || '',
        status: frCarrier.status || 'rendered',
        fallback: false,
      };
    }
  }

  // English fallback
  const enCarrier = getCarrier(chronicle, 'en') || chronicle.youtube || {};
  return {
    youtubeUrl: enCarrier.youtubeUrl || chronicle.youtubeUrl || chronicle.youtube?.youtubeUrl || '',
    youtubeVideoId: enCarrier.youtubeVideoId || enCarrier.youtubeVideo || chronicle.youtubeVideoId || chronicle.youtube?.youtubeVideoId || '',
    videoPath: enCarrier.videoPath || chronicle.videoPath || '',
    status: enCarrier.status || chronicle.youtube?.status || 'idle',
    fallback: true,
  };
};
